import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { User } from '../models/User';
import { UserDto } from '../interfaces/dto/user.dto';
import { EnvironmentService } from '../services/environment.service';
import { catchError, map, tap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { AppState } from '../state/reducers';
import * as Actions from './state/actions';
import { OauthToken } from './interfaces/oauth-token';
import { TokenRequestParams } from './interfaces/token-request-params';
import { UserAdapter } from '../adapters/user.adapter';
import {ClearAvailablePractices, GetUserPractices} from '../practices/state/actions';
import { getUser } from './state/selectors';
import { Location } from '@angular/common';
import {GetUnreadHelpPosts, Logout} from './state/actions';
import { getOneSignalUserId, isEmbedMode } from '../state/selectors';
import {Router} from '@angular/router';
import {HelpPostDto} from '../interfaces/dto/help-post.dto';
import { SetEmbedPartner } from '../state/actions';
import { embedPartnerAdapter } from '../adapters/embed-partner.adapter';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  oneSignalUserId: string | null = null;
  user: User | null = null;
  embedMode$?: Observable<boolean>;
  embedMode?: boolean;

  constructor(
    private router: Router,
    private http: HttpClient,
    private environmentService: EnvironmentService,
    private cookieService: CookieService,
    private userAdapter: UserAdapter,
    private store: Store<AppState>,
    private location: Location
  ) {
    this.subscribeToOneSignal();
    this.suscribeToEmbedMode();
  }

  suscribeToEmbedMode(): void {
    this.embedMode$ = this.store
      .pipe(select(isEmbedMode));

    this.embedMode$.subscribe(embedMode => this.embedMode = embedMode)
  }

  hasJwtCookie(): boolean {
    return this.cookieService.check(this.environmentService.get('jwtPrefix') + 'JWT');
  }

  hasCoreJwtCookie(): boolean {
    return this.cookieService.check(this.environmentService.get('jwtPrefix') + 'CORE_JWT');
  }

  getUser(): Observable<User> {
    return this.http.get<UserDto>(this.environmentService.get('backendUrl') + '/auth/user').pipe(
      map((response: UserDto) => this.userAdapter.run(response))
    );
  }

  getAndStoreUser(): void {
    this.getUser().subscribe((user) => {
      this.user = user;
      this.storeUser(user);
      this.updateUserDevices();
    }, (e) => {
      console.log(e);
    });
  }

  getUserUnreadHelpPosts(): Observable<HelpPostDto[]> {
    return this.http.get<HelpPostDto[]>(this.environmentService.get('authUrl') + '/api/v1/learn/section/digital-practice/latest-posts', {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${this.cookieService.get('access_token')}`
      }
    });
  }

  markHelpPostsSeen(): Observable<boolean> {
    return this.http.get<boolean>(this.environmentService.get('authUrl') + '/api/v1/learn/section/digital-practice/mark-latest-read', {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${this.cookieService.get('access_token')}`
      }
    });
  }

  grantPracticeAccess(practiceId: string, userId: string): Observable<any> {
    const url = this.environmentService.get('backendUrl') + `/practices/${practiceId}/grant-access`;
    const body = { userId };
    const response = this.http.post(url, body, {withCredentials: true, observe: 'response'});
    return response;
  }

  recoverSession(): boolean {
    this.store.pipe(select(getUser)).subscribe((user) => {
      if (user) {
        this.store.dispatch(GetUserPractices());
      }
    });

    if (!this.location.path().includes('auth/callback')) {
      this.getAndStoreUser();
      this.store.dispatch(GetUnreadHelpPosts());
    }

    return true;
  }

  storeUser(user: any): void {
    this.store.dispatch(Actions.StoreUser({user}));
  }

  refreshToken(): Observable<OauthToken>  {
    if (this.embedMode) {
      window.location.reload();
    }

    return this.http.post<OauthToken>(this.environmentService.get('authUrl') + '/api/v1/oauth/token', {
      grant_type: 'refresh_token',
      client_id: this.environmentService.get('authClientId'),
    }, {withCredentials: true});
  }

  getToken(params: TokenRequestParams): Observable<OauthToken>  {
    return this.http.post<OauthToken>(this.environmentService.get('authUrl') + '/api/v1/oauth/token', params, {withCredentials: true});
  }

  getJwt(login?: boolean): Observable<Response> {
    let route = '/auth/request-token';
    if (login) {
      route += '?login=1';
    }
    return this.http.get<Response>(this.environmentService.get('backendUrl') + route, {withCredentials: true});
  }

  getCoreJwt(): Observable<Response> {
    return this.http.get<Response>(this.environmentService.get('authUrl') + '/api/v1/auth/jwt', {withCredentials: true});
  }

  logout(): void {
    this.doLogout().subscribe((response: {url: string}) => {
      setTimeout(() => {
        if(response?.url) {
          window.location.href = response.url;
        }
        else{
          this.router.navigate(['/auth/login']);
        }
      }, 0);
      this.store.dispatch(ClearAvailablePractices());
      setTimeout(() => {
        this.store.dispatch(Logout());
      }, 1000);
    }, (e) => {
      console.log(e);
    });
  }

  doLogout(): Observable<Response> {
    this.removeUserDevice();
    return this.http.get<Response>(this.environmentService.get('authUrl') + '/api/v1/auth/logout', {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${this.cookieService.get('access_token')}`
      }
    });
  }

  removeUserDevice(): void {
    if (this.oneSignalUserId && this.user) {
      this.http.delete<Response>(this.environmentService.get('backendUrl') + '/users/' + this.user.id + '/devices/' + this.oneSignalUserId, {withCredentials: true}).subscribe((response) => {});
    }
  }

  updateUserDevices(): void {
    if (this.oneSignalUserId && this.user) {
      this.http.post<Response>(this.environmentService.get('backendUrl') + '/users/' + this.user.id + '/devices', {
        deviceId: this.oneSignalUserId
      }, {withCredentials: true}).subscribe((response) => {});
    }
  }

  subscribeToOneSignal(): void {
    this.store.pipe(select(getOneSignalUserId)).subscribe((oneSignalUserId: string | null) => {
      this.oneSignalUserId = oneSignalUserId;
    });
  }

  handleEmbedAuth(pms: string, pmsId: string): Observable<boolean> {
    this.store.dispatch(SetEmbedPartner({partner: embedPartnerAdapter(pms)}));

    if (this.hasCoreJwtCookie()) {
      this.store.pipe(select(getUser)).subscribe((user) => {
        if (user) {
          this.store.dispatch(GetUserPractices());
        }
      });

      this.getAndStoreUser();
      return of(true);
    }

    return this.getCoreJwt().pipe(
      tap(() => {
        if (this.hasCoreJwtCookie()) {
          this.store.pipe(select(getUser)).subscribe((user) => {
            if (user) {
              this.store.dispatch(GetUserPractices());
            }
          });
          this.getAndStoreUser();
        }
      }),
      map(() => true),
      catchError((err) => {
        console.error('JWT fetch failed, falling back to OAuth:', err);
        switch (pms) {
          case 'provet':
            console.log('could not get core JWT, redirecting')
            this.provetOAuthFlow(pmsId);
            return of(true);
          default:
            return of(false);
        }
      })
    );
  }

  provetOAuthFlow(cloudId: string): void {
    const currentUrl = window.location.href;
    window.location.href = `${this.environmentService.get('authUrl')}/embed-auth/provet/start?cloudId=${cloudId}&return_to=${encodeURIComponent(currentUrl)}`;
  }
}
